import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "@/integrations/lovable/client";
import { prefetchHotRoutes } from "./lib/prefetchRoutes";

// Critical fonts — needed for above-the-fold content (FCP)
import "@fontsource/sora/latin-600.css";
import "@fontsource/sora/latin-700.css";
import "@fontsource/ibm-plex-sans/latin-400.css";

// Hero LCP image preload moved to index.html so it's discoverable during
// HTML parse (saves ~7s of resource-load delay vs. injecting from JS).
// Critical display font for the H1 — preloading eliminates the post-paint
// font swap that drives CLS on the landing hero.
import soraBold from "@fontsource/sora/files/sora-latin-700-normal.woff2?url";

// Disable browser's automatic scroll restoration. On long lazy-loaded
// landing pages it restores to the prior scroll position before chunks
// hydrate, then ScrollToTop yanks back to 0 — producing a visible
// "down then up" jump on first paint.
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

const isLandingRoute =
  typeof window !== "undefined" &&
  (window.location.pathname === "/" || window.location.pathname === "");

if (isLandingRoute && typeof document !== "undefined") {
  const head = document.head;
  // Preload Sora 700 (H1 display font) to avoid the layout shift caused by
  // the late font swap.
  const fontPreload = document.createElement("link");
  fontPreload.rel = "preload";
  fontPreload.as = "font";
  fontPreload.type = "font/woff2";
  fontPreload.href = soraBold;
  fontPreload.crossOrigin = "anonymous";
  head.appendChild(fontPreload);
}

import "./index.css";

// Defer non-critical font weights to after initial paint
const loadDeferredFonts = () => {
  import("@fontsource/sora/latin-400.css");
  import("@fontsource/sora/latin-500.css");
  import("@fontsource/ibm-plex-sans/latin-500.css");
  import("@fontsource/ibm-plex-sans/latin-600.css");
  import("@fontsource/ibm-plex-sans/latin-700.css");
  import("@fontsource/ibm-plex-mono/latin-400.css");
  import("@fontsource/ibm-plex-mono/latin-500.css");
  // Brand evolution (Phase 1) — premium hardware typography stack.
  // Barlow Condensed = display stencil ("RACK & ROLE" headings, mono-cap eyebrows).
  // Plus Jakarta Sans = humanist body counterpart to industrial display.
  // JetBrains Mono = tabular numerics + telemetry chips.
  import("@fontsource/barlow-condensed/latin-600.css");
  import("@fontsource/barlow-condensed/latin-700.css");
  import("@fontsource/plus-jakarta-sans/latin-400.css");
  import("@fontsource/plus-jakarta-sans/latin-500.css");
  import("@fontsource/plus-jakarta-sans/latin-600.css");
  import("@fontsource/jetbrains-mono/latin-400.css");
  import("@fontsource/jetbrains-mono/latin-500.css");
};
if ("requestIdleCallback" in window) {
  requestIdleCallback(loadDeferredFonts);
} else {
  setTimeout(loadDeferredFonts, 100);
}

createRoot(document.getElementById("root")!).render(<App />);

// Warm hot route chunks during idle so subsequent navigation is instant.
prefetchHotRoutes();
